'use client';

import { CircularProgress, Divider, Stack, Typography } from '@mui/material';
import type {
  BucketPathPartitionSpec,
  IngestionPipeline,
} from '@repo/api-gw-sdk';
import { PartitionFormat, PartitionSegmentType } from '@repo/api-gw-sdk';
import { useMemo, useState } from 'react';

import { OptionMenu } from '@/components/option-menu/option-menu';
import { PanelWrapper } from '@/components/panels/panelWrapper';
import { Panels } from '@/components/panels/panels';
import { useDAL } from '@/contexts/useDAL';
import { useRoles } from '@/contexts/useRoles';
import { useWorkspace as useGlobalWorkspace } from '@/contexts/useWorkspace';
import { PanelSize } from '@/contexts/workspaceContext';
import { dayjs } from '@/utils/dayjs';

import { DeleteIngestionPipelineDialog } from './deleteIngestionPipelineDialog';
import { IngestionPipelineStatusCell } from './ingestionPipelineStatusCell';
import type { PathSegmentState } from './wizard/IngestionPipelineWizardState';
import { PathBuilder } from './wizard/partitioning/PathBuilder';

interface IngestionPipelinePanelProps {
  id: string;
  workspaceId: string;
  onClose: () => void;
  onRefresh: () => void;
}

export const IngestionPipelinePanel = ({
  id,
  workspaceId,
  onClose,
  onRefresh,
}: IngestionPipelinePanelProps) => {
  const dal = useDAL();
  const { isAuthorized } = useRoles();
  const [isLoading, setIsLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { rightPanel } = useGlobalWorkspace();

  const { data: pipelinesData, isLoading: isPipelinesLoading } =
    dal.lakehouse.useListIngestionPipelines(workspaceId);

  const pipeline = pipelinesData?.ingestionPipelines?.find(
    (p: IngestionPipeline) => p.id === id
  );

  const sourceProps =
    pipeline?.s3SourceProperties ?? pipeline?.gcsSourceProperties;
  const partitionSpec = sourceProps?.bucketPathPartitionSpec;

  const segments = useMemo(
    () => toPathSegmentStates(partitionSpec),
    [partitionSpec]
  );

  if (isPipelinesLoading || !pipeline) {
    return (
      <PanelWrapper
        header={{
          title: 'Ingestion Pipeline Details',
          onClose,
        }}
      >
        <Stack alignItems='center' justifyContent='center' flex={1}>
          <CircularProgress />
        </Stack>
      </PanelWrapper>
    );
  }

  const canUpdate = isAuthorized('update:ingestion_pipeline');
  const canDelete = isAuthorized('delete:ingestion_pipeline');

  const isStarted = pipeline.status === 'STARTED';
  const isStopped = pipeline.status === 'STOPPED';
  const isTransitioning =
    pipeline.status === 'CREATING' ||
    pipeline.status === 'STARTING' ||
    pipeline.status === 'STOPPING' ||
    pipeline.status === 'DELETING';

  const handleStart = async () => {
    setIsLoading(true);
    try {
      await dal.lakehouse.startIngestionPipeline(workspaceId, pipeline.id);
      onRefresh();
    } catch (err) {
      console.error('Failed to start pipeline:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStop = async () => {
    setIsLoading(true);
    try {
      await dal.lakehouse.stopIngestionPipeline(workspaceId, pipeline.id);
      onRefresh();
    } catch (err) {
      console.error('Failed to stop pipeline:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = () => {
    rightPanel.setIsOpen(true, PanelSize.Full);
    rightPanel.setComponent({
      panel: Panels.UpdateIngestionPipelineWizard,
      props: {
        workspaceId,
        pipeline,
        onAbort: () => {
          // Re-open the instance panel after aborting edit
          rightPanel.setIsOpen(true, PanelSize.Large);
          rightPanel.setComponent({
            panel: Panels.IngestionPipelineInstance,
            props: { id, workspaceId, onClose, onRefresh },
          });
        },
        onUpdated: () => onRefresh(),
      },
    });
  };

  return (
    <>
      <PanelWrapper
        header={{
          title: 'Ingestion Pipeline Details',
          onClose,
          actions: isLoading ? (
            <CircularProgress size={20} />
          ) : (
            <OptionMenu
              options={[
                {
                  id: 'edit-pipeline',
                  text: 'Edit',
                  icon: 'material-symbols-edit-outline-rounded',
                  menuItemProps: {
                    disabled: !canUpdate || !isStopped,
                    onClick: handleEdit,
                  },
                },
                {
                  id: 'start-pipeline',
                  text: 'Start',
                  icon: 'material-symbols-play-arrow-rounded',
                  menuItemProps: {
                    disabled: !canUpdate || isTransitioning || !isStopped,
                    onClick: () => void handleStart(),
                  },
                },
                {
                  id: 'stop-pipeline',
                  text: 'Stop',
                  icon: 'material-symbols-stop-rounded',
                  menuItemProps: {
                    disabled: !canUpdate || isTransitioning || !isStarted,
                    onClick: () => void handleStop(),
                  },
                },
                {
                  id: 'divider',
                  divider: true,
                },
                {
                  id: 'delete-pipeline',
                  text: 'Delete',
                  icon: 'material-symbols-delete-outline-rounded',
                  menuItemProps: {
                    disabled: !canDelete || isTransitioning,
                    onClick: () => setDeleteDialogOpen(true),
                  },
                },
              ]}
            />
          ),
        }}
      >
        <Stack gap='24px' padding='24px 40px'>
          {/* Pipeline Details */}
          <Stack gap='12px'>
            <DetailRow label='Name' value={pipeline.name} />
            <DetailRow label='Namespace' value={pipeline.namespace} />
            <DetailRow label='Table Name' value={pipeline.tableName} />
            <DetailRow
              label='Status'
              value={<IngestionPipelineStatusCell status={pipeline.status} />}
            />
            <DetailRow
              label='Created'
              value={dayjs
                .utc(pipeline.createdAt)
                .format('MMM D, YYYY HH:mm:ss')}
            />
            {pipeline.failureMessage && (
              <DetailRow
                label='Error'
                value={
                  <Typography
                    variant='body2'
                    color='error'
                    sx={{ wordBreak: 'break-word', flex: 1 }}
                  >
                    {pipeline.failureMessage}
                  </Typography>
                }
              />
            )}
          </Stack>

          {/* S3 Source Configuration */}
          {pipeline.s3SourceProperties && (
            <>
              <Divider />
              <Stack gap='12px'>
                <Typography variant='subtitle2' color='text.secondary'>
                  S3 Source
                </Typography>
                <DetailRow
                  label='Bucket'
                  value={pipeline.s3SourceProperties.bucket}
                />
                <DetailRow
                  label='Region'
                  value={pipeline.s3SourceProperties.region}
                />
                <DetailRow
                  label='Account ID'
                  value={pipeline.s3SourceProperties.ownerAccountId}
                />
                <DetailRow
                  label='File Format'
                  value={pipeline.s3SourceProperties.fileFormat}
                />
              </Stack>
            </>
          )}

          {/* GCS Source Configuration */}
          {pipeline.gcsSourceProperties && (
            <>
              <Divider />
              <Stack gap='12px'>
                <Typography variant='subtitle2' color='text.secondary'>
                  GCS Source
                </Typography>
                <DetailRow
                  label='Bucket'
                  value={pipeline.gcsSourceProperties.bucket}
                />
                <DetailRow
                  label='Location'
                  value={pipeline.gcsSourceProperties.location}
                />
                <DetailRow
                  label='Project ID'
                  value={pipeline.gcsSourceProperties.projectId}
                />
                <DetailRow
                  label='File Format'
                  value={pipeline.gcsSourceProperties.fileFormat}
                />
              </Stack>
            </>
          )}

          {/* Partitioning */}
          {partitionSpec && segments.length > 0 && (
            <>
              <Divider />
              <Stack gap='12px'>
                <Typography variant='subtitle2' color='text.secondary'>
                  Partitioning
                </Typography>
                <PathBuilder
                  segments={segments}
                  separator={partitionSpec.separator ?? '/'}
                  readOnly
                />
              </Stack>
            </>
          )}
        </Stack>
      </PanelWrapper>

      <DeleteIngestionPipelineDialog
        pipeline={pipeline}
        workspaceId={workspaceId}
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onDeleted={() => {
          onRefresh();
          onClose();
        }}
      />
    </>
  );
};

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}) => {
  return (
    <Stack direction='row' alignItems='flex-start' gap='16px'>
      <Typography
        variant='body2'
        color='text.secondary'
        sx={{ minWidth: '100px' }}
      >
        {label}
      </Typography>
      {typeof value === 'string' ? (
        <Typography variant='body2' sx={{ wordBreak: 'break-word', flex: 1 }}>
          {value || '-'}
        </Typography>
      ) : (
        (value ?? (
          <Typography variant='body2' sx={{ flex: 1 }}>
            -
          </Typography>
        ))
      )}
    </Stack>
  );
};

function toPathSegmentStates(
  spec: BucketPathPartitionSpec | undefined
): PathSegmentState[] {
  if (!spec?.segments) return [];
  return spec.segments.map((seg, i) => ({
    id: `seg-${i}`,
    segmentType: seg.segmentType,
    segmentStringValue: seg.segmentStringValue ?? '',
    segmentPartitionName: seg.segmentPartitionName ?? '',
    segmentPartitionType:
      seg.segmentPartitionType ??
      (PartitionSegmentType.PARTITION_SEGMENT_TYPE_SECONDARY as PathSegmentState['segmentPartitionType']),
    segmentPartitionFormat:
      seg.segmentPartitionFormat ??
      (PartitionFormat.PARTITION_FORMAT_HIVE as PathSegmentState['segmentPartitionFormat']),
  }));
}
